import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import SessionTypeFields from 'components/session-type/SessionTypeFields';
import defaultSessionTypeFormValidations from 'common/validations/form/defaultSessionType';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { updateSessionTypeInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { getFieldsToUpdate } from 'common/form/utils';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import sessionTypeSlice from 'store/modules/sessionType';

const requiredFields = ['name', 'price'];

function SessionTypeEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes']);
  const pageTitle = t('sessionType.edit', { ns: 'routes' });
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const sessionType = useSelector((state) => state.sessionType.currentSessionType);
  const status = useSelector((state) => state.sessionType.status);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten el mismo status; sin este guard el find inicial rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(sessionTypeSlice.actions.find(id));
  }, []);

  useEffect(() => {
    if (sessionType && Object.keys(sessionType).length > 0 && sessionType.id === id) {
      const sessionTypeData = updateSessionTypeInitialValues(sessionType);
      setInitialValues(sessionTypeData);
    }
  }, [sessionType]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listSessionType.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'sessionType');
      hasSubmitted.current = true;
      dispatch(sessionTypeSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultSessionTypeFormValidations}
        render={({ handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <SessionTypeFields requiredFields={requiredFields} />
            <StyledButtonsContainer>
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listSessionType.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('sessionType.edit', { ns: 'buttons' })}
                type="submit"
              />
            </StyledButtonsContainer>
          </form>
        )}
      />
    </>
  );
}

export default SessionTypeEdit;
